'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function CashBackOrLowInterestCalculator() {
  const t = useTranslations("tools.cashBackOrLowInterestCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    cashback: 1000,
    highRate: 5,
    lowRate: 2,
    autoPrice: 50000,
    loanTerm: 60,
    downPayment: 10000,
    tradeIn: 0,
    salesTax: 7,
    fees: 2000,
    includeFeesInLoan: false,
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const calculateMonthlyPayment = (principal: number, annualRate: number, months: number) => {
    if (annualRate === 0) {
      return principal / months;
    }
    const monthlyRate = annualRate / 100 / 12;
    return principal * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
  };

  const calculateOption = (autoPrice: number, cashBack: number, interestRate: number, loanTerm: number, downPayment: number, tradeIn: number, salesTax: number, fees: number, includeFeesInLoan: boolean) => {
    const priceAfterRebate = autoPrice - cashBack;
    const taxableAmount = autoPrice;
    const salesTaxAmount = taxableAmount * (salesTax / 100);

    let loanAmount;
    let upfrontPayment;

    if (includeFeesInLoan) {
      loanAmount = priceAfterRebate + salesTaxAmount + fees - downPayment - tradeIn;
      upfrontPayment = downPayment + tradeIn;
    } else {
      loanAmount = priceAfterRebate - downPayment - tradeIn;
      upfrontPayment = downPayment + tradeIn + salesTaxAmount + fees;
    }

    loanAmount = Math.max(0, loanAmount);

    const monthlyPayment = calculateMonthlyPayment(loanAmount, interestRate, loanTerm);
    const totalPayments = monthlyPayment * loanTerm;
    const totalInterest = totalPayments - loanAmount;
    const totalCost = autoPrice + totalInterest + salesTaxAmount + fees;

    return {
      loanAmount,
      salesTaxAmount,
      upfrontPayment,
      monthlyPayment,
      totalPayments,
      totalInterest,
      totalCost
    };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (inputs.autoPrice <= 0) {
      setError(t('errorInvalidPrice'));
      return;
    }

    if (inputs.lowRate >= inputs.highRate) {
      setError(t('errorRateComparison'));
      return;
    }

    const cashBackOption = calculateOption(
      inputs.autoPrice,
      inputs.cashback,
      inputs.highRate,
      inputs.loanTerm,
      inputs.downPayment,
      inputs.tradeIn,
      inputs.salesTax,
      inputs.fees,
      inputs.includeFeesInLoan
    );

    const lowRateOption = calculateOption(
      inputs.autoPrice,
      0,
      inputs.lowRate,
      inputs.loanTerm,
      inputs.downPayment,
      inputs.tradeIn,
      inputs.salesTax,
      inputs.fees,
      inputs.includeFeesInLoan
    );

    const interestSavings = cashBackOption.totalInterest - lowRateOption.totalInterest;
    const totalSavings = cashBackOption.totalCost - lowRateOption.totalCost;

    setResults({
      cashBackOption,
      lowRateOption,
      interestSavings,
      totalSavings,
      recommendation: totalSavings > 0 ? 'lowRate' : 'cashBack'
    });
  };

  const handleClear = () => {
    setInputs({
      cashback: 1000,
      highRate: 5,
      lowRate: 2,
      autoPrice: 50000,
      loanTerm: 60,
      downPayment: 10000,
      tradeIn: 0,
      salesTax: 7,
      fees: 2000,
      includeFeesInLoan: false,
    });
    setResults(null);
    setError('');
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '1.5rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem', marginBottom: '2rem' },
    { padding: '2.5rem', borderRadius: '1.5rem', marginBottom: '2.5rem' }
  );

  const formSectionStyle = {
    marginBottom: '1.5rem'
  };

  const sectionTitleStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      color: '#2563eb',
      marginBottom: '1rem',
      paddingBottom: '0.5rem',
      borderBottom: '2px solid #e0f2fe'
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const formGroupStyle = {
    marginBottom: '1rem'
  };

  const labelStyle = getResponsiveStyle(
    {
      display: 'block',
      fontWeight: 500,
      color: '#334155',
      marginBottom: '0.5rem'
    },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1.125rem' },
    { padding: '1rem', fontSize: '1.125rem' }
  );

  const inputWithUnitStyle = {
    position: 'relative' as const
  };

  const inputUnitStyle = getResponsiveStyle(
    {
      position: 'absolute' as const,
      right: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
      pointerEvents: 'none' as const
    },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' },
    { fontSize: '1rem' }
  );

  const checkboxGroupStyle = {
    display: 'flex',
    alignItems: 'center',
    marginTop: '1rem'
  };

  const checkboxStyle = {
    width: 'auto',
    marginRight: '0.5rem',
    cursor: 'pointer'
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '1.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const, marginTop: '2rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', minHeight: '44px' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.125rem 2.5rem', fontSize: '1.125rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', minHeight: '44px' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.125rem 2.5rem', fontSize: '1.125rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    {
      background: '#fef2f2',
      color: '#991b1b',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
      borderLeft: '4px solid #dc2626'
    },
    { padding: '0.875rem', fontSize: '0.875rem' },
    { padding: '1rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const recommendationStyle = getResponsiveStyle(
    {
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      fontWeight: 600,
      textAlign: 'center' as const
    },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1.25rem', fontSize: '1.125rem' },
    { padding: '1.25rem', fontSize: '1.25rem' }
  );

  const savingsInfoStyle = getResponsiveStyle(
    {
      background: '#f0f9ff',
      borderRadius: '0.5rem',
      marginBottom: '1.5rem',
      color: '#0c4a6e',
      textAlign: 'center' as const
    },
    { padding: '0.875rem', fontSize: '0.875rem' },
    { padding: '1rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const comparisonGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const optionCardStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderRadius: '0.75rem',
      border: '2px solid #e2e8f0'
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    borderBottom: '1px solid #e2e8f0'
  };

  const resultLabelStyle = getResponsiveStyle(
    { color: '#64748b' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const resultValueHighlightStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#2563eb' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { marginLeft: '1.5rem', marginBottom: '1rem', color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem',
    lineHeight: 1.6
  };

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const referenceListStyle = getResponsiveStyle(
    { listStyleType: 'none', padding: 0 },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const referenceItemStyle = {
    marginBottom: '0.75rem',
    paddingLeft: '1.5rem',
    position: 'relative' as const
  };

  const referenceLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={h2Style}>{t('toolTitle')}</h2>
          <p style={summaryTextStyle}>{t('toolSubtitle')}</p>
        </div>

        <div style={calculatorLayoutStyle}>
          <div style={glassCardStyle}>
            <form onSubmit={handleCalculate}>
              <div style={formSectionStyle}>
                <div style={sectionTitleStyle}>{t('cashBackOfferTitle')}</div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="cashback">{t('cashBackAmount')}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="cashback"
                      value={inputs.cashback}
                      onChange={(e) => setInputs({...inputs, cashback: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="100"
                      style={inputStyle}
                      required
                    />
                    <span style={inputUnitStyle}>$</span>
                  </div>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="highRate">{t('interestRateHigh')}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="highRate"
                      value={inputs.highRate}
                      onChange={(e) => setInputs({...inputs, highRate: parseFloat(e.target.value) || 0})}
                      min="0"
                      max="30"
                      step="0.1"
                      style={inputStyle}
                      required
                    />
                    <span style={inputUnitStyle}>%</span>
                  </div>
                </div>
              </div>

              <div style={formSectionStyle}>
                <div style={sectionTitleStyle}>{t('lowInterestRateTitle')}</div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="lowRate">{t('interestRateLow')}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="lowRate"
                      value={inputs.lowRate}
                      onChange={(e) => setInputs({...inputs, lowRate: parseFloat(e.target.value) || 0})}
                      min="0"
                      max="30"
                      step="0.1"
                      style={inputStyle}
                      required
                    />
                    <span style={inputUnitStyle}>%</span>
                  </div>
                </div>
              </div>

              <div style={formSectionStyle}>
                <div style={sectionTitleStyle}>{t('otherInformationTitle')}</div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="autoPrice">{t('autoPrice')}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="autoPrice"
                      value={inputs.autoPrice}
                      onChange={(e) => setInputs({...inputs, autoPrice: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="1000"
                      style={inputStyle}
                      required
                    />
                    <span style={inputUnitStyle}>$</span>
                  </div>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="loanTerm">{t('loanTerm')}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="loanTerm"
                      value={inputs.loanTerm}
                      onChange={(e) => setInputs({...inputs, loanTerm: parseInt(e.target.value) || 60})}
                      min="12"
                      max="96"
                      step="12"
                      style={inputStyle}
                      required
                    />
                    <span style={inputUnitStyle}>{t('months')}</span>
                  </div>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="downPayment">{t('downPayment')}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="downPayment"
                      value={inputs.downPayment}
                      onChange={(e) => setInputs({...inputs, downPayment: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="500"
                      style={inputStyle}
                      required
                    />
                    <span style={inputUnitStyle}>$</span>
                  </div>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="tradeIn">{t('tradeInValue')}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="tradeIn"
                      value={inputs.tradeIn}
                      onChange={(e) => setInputs({...inputs, tradeIn: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="500"
                      style={inputStyle}
                    />
                    <span style={inputUnitStyle}>$</span>
                  </div>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="salesTax">{t('salesTax')}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="salesTax"
                      value={inputs.salesTax}
                      onChange={(e) => setInputs({...inputs, salesTax: parseFloat(e.target.value) || 0})}
                      min="0"
                      max="15"
                      step="0.1"
                      style={inputStyle}
                      required
                    />
                    <span style={inputUnitStyle}>%</span>
                  </div>
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="fees">{t('fees')}</label>
                  <div style={inputWithUnitStyle}>
                    <input
                      type="number"
                      id="fees"
                      value={inputs.fees}
                      onChange={(e) => setInputs({...inputs, fees: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="100"
                      style={inputStyle}
                      required
                    />
                    <span style={inputUnitStyle}>$</span>
                  </div>
                </div>
                <div style={checkboxGroupStyle}>
                  <input
                    type="checkbox"
                    id="includeFeesInLoan"
                    checked={inputs.includeFeesInLoan}
                    onChange={(e) => setInputs({...inputs, includeFeesInLoan: e.target.checked})}
                    style={checkboxStyle}
                  />
                  <label style={{...labelStyle, marginBottom: 0, fontWeight: 400}} htmlFor="includeFeesInLoan">
                    {t('includeFeesInLoan')}
                  </label>
                </div>
              </div>

              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateButtonStyle}>
                  {t('calculateButton')}
                </button>
                <button type="button" onClick={handleClear} style={clearButtonStyle}>
                  {t('clearButton')}
                </button>
              </div>
            </form>
          </div>

          {(results || error) && (
            <div style={glassCardStyle}>
              <h3 style={h2Style}>{t('resultsTitle')}</h3>

              {error && (
                <div style={errorMessageStyle}>{error}</div>
              )}

              {results && (
                <>
                  <div style={{
                    ...recommendationStyle,
                    background: results.recommendation === 'lowRate'
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                    color: 'white'
                  }}>
                    {results.recommendation === 'lowRate' ? t('recommendationLowRate') : t('recommendationCashBack')}
                  </div>

                  <div style={savingsInfoStyle}>
                    {results.recommendation === 'lowRate'
                      ? t('savingsInfoLowRate', {
                          interestSavings: formatCurrency(results.interestSavings),
                          cashback: formatCurrency(inputs.cashback),
                          totalSavings: formatCurrency(results.totalSavings)
                        })
                      : t('savingsInfoCashBack', {
                          cashback: formatCurrency(inputs.cashback),
                          interestSavings: formatCurrency(results.interestSavings),
                          totalSavings: formatCurrency(Math.abs(results.totalSavings))
                        })
                    }
                  </div>

                  <div style={comparisonGridStyle}>
                    <div style={optionCardStyle}>
                      <h4 style={{...h3Style, marginTop: 0}}>{t('withCashBackOffer')}</h4>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('totalLoanAmount')}</span>
                        <span style={resultValueStyle}>{formatCurrency(results.cashBackOption.loanAmount)}</span>
                      </div>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('salesTaxLabel')}</span>
                        <span style={resultValueStyle}>{formatCurrency(results.cashBackOption.salesTaxAmount)}</span>
                      </div>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('upfrontPayment')}</span>
                        <span style={resultValueStyle}>{formatCurrency(results.cashBackOption.upfrontPayment)}</span>
                      </div>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('monthlyPayment')}</span>
                        <span style={resultValueHighlightStyle}>{formatCurrency(results.cashBackOption.monthlyPayment)}</span>
                      </div>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('totalOfPayments')}</span>
                        <span style={resultValueStyle}>{formatCurrency(results.cashBackOption.totalPayments)}</span>
                      </div>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('totalLoanInterest')}</span>
                        <span style={resultValueStyle}>{formatCurrency(results.cashBackOption.totalInterest)}</span>
                      </div>
                      <div style={{...resultItemStyle, borderBottom: 'none'}}>
                        <span style={resultLabelStyle}>{t('totalCost')}</span>
                        <span style={resultValueHighlightStyle}>{formatCurrency(results.cashBackOption.totalCost)}</span>
                      </div>
                    </div>

                    <div style={optionCardStyle}>
                      <h4 style={{...h3Style, marginTop: 0}}>{t('withLowInterestRate')}</h4>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('totalLoanAmount')}</span>
                        <span style={resultValueStyle}>{formatCurrency(results.lowRateOption.loanAmount)}</span>
                      </div>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('salesTaxLabel')}</span>
                        <span style={resultValueStyle}>{formatCurrency(results.lowRateOption.salesTaxAmount)}</span>
                      </div>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('upfrontPayment')}</span>
                        <span style={resultValueStyle}>{formatCurrency(results.lowRateOption.upfrontPayment)}</span>
                      </div>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('monthlyPayment')}</span>
                        <span style={resultValueHighlightStyle}>{formatCurrency(results.lowRateOption.monthlyPayment)}</span>
                      </div>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('totalOfPayments')}</span>
                        <span style={resultValueStyle}>{formatCurrency(results.lowRateOption.totalPayments)}</span>
                      </div>
                      <div style={resultItemStyle}>
                        <span style={resultLabelStyle}>{t('totalLoanInterest')}</span>
                        <span style={resultValueStyle}>{formatCurrency(results.lowRateOption.totalInterest)}</span>
                      </div>
                      <div style={{...resultItemStyle, borderBottom: 'none'}}>
                        <span style={resultLabelStyle}>{t('totalCost')}</span>
                        <span style={resultValueHighlightStyle}>{formatCurrency(results.lowRateOption.totalCost)}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToUseStep1')}</li>
          <li style={listItemStyle}>{t('howToUseStep2')}</li>
          <li style={listItemStyle}>{t('howToUseStep3')}</li>
          <li style={listItemStyle}>{t('howToUseStep4')}</li>
          <li style={listItemStyle}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>

        <h3 style={h3Style}>{t('insightsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('insightsSubParagraph1')}</p>

        <h3 style={h3Style}>{t('insightsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('insightsSubParagraph2')}</p>

        <h3 style={h3Style}>{t('insightsSubtitle3')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insightsBestPractice1')}</li>
          <li style={listItemStyle}>{t('insightsBestPractice2')}</li>
          <li style={listItemStyle}>{t('insightsBestPractice3')}</li>
          <li style={listItemStyle}>{t('insightsBestPractice4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsSubParagraph1')}</p>

        <h3 style={h3Style}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsSubParagraph2')}</p>

        <h3 style={h3Style}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsSubParagraph3')}</p>

        <h3 style={h3Style}>{t('detailsSubtitle4')}</h3>
        <p style={paragraphStyle}>{t('detailsSubParagraph4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={h3Style}>{t('faqQuestion1')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer1')}</p>

        <h3 style={h3Style}>{t('faqQuestion2')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer2')}</p>

        <h3 style={h3Style}>{t('faqQuestion3')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer3')}</p>

        <h3 style={h3Style}>{t('faqQuestion4')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer4')}</p>

        <h3 style={h3Style}>{t('faqQuestion5')}</h3>
        <p style={paragraphStyle}>{t('faqAnswer5')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={referenceListStyle}>
          <li style={referenceItemStyle}>
            <a href="https://banzai.org/wellness/resources/low-rate-cash-back-calculator" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              {t('reference1')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://www.edmunds.com/calculators/incentives-rebates.html" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              {t('reference2')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://www.calculator.net/cash-back-or-low-interest-calculator.html" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              {t('reference3')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://www.mysccu.com/learn/cash-back-vs-lower-interest-financing" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              {t('reference4')}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://www.regions.com/insights/personal/calculator/should-i-choose-low-rate-or-cash-back" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              {t('reference5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}